import type { PipeTransform } from '@angular/core';
import { Pipe, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import type { OnyxOptional } from '@onyx/angular';
import { OnyxTimePipe } from '@onyx/angular';
import { isEqual } from 'lodash';
import { DAY_TIME_RANGE } from '../constants/common/day-time-range';
import type { BusinessHours } from '../interfaces/common/business-hours';

@Pipe({
  name: 'businessHours',
})
export class BusinessHoursPipe implements PipeTransform {
  private readonly translateService = inject(TranslateService);
  private readonly timePipe = inject(OnyxTimePipe);

  public transform(
    businessHours: OnyxOptional<BusinessHours>,
    key?: keyof BusinessHours,
    format?: 'short' | 'long',
    only?: 'label' | 'value',
  ): string {
    if (!businessHours) return '';

    format ??= 'short';

    const formatRange = (key: keyof BusinessHours): string => {
      const label = this.translateService.instant(
        `labels.${key}${format === 'short' ? 'Shortcut' : ''}`,
      );
      if (only === 'label') return label;

      const value = businessHours[key]
        ? this.timePipe.transform(businessHours[key])
        : format === 'short'
          ? '-'
          : this.translateService.instant('labels.closed');
      if (only === 'value') return value;

      return `${label}: ${value}`;
    };

    if (key) return formatRange(key);

    if (
      Object.values(businessHours).every((openingHours) =>
        isEqual(openingHours, DAY_TIME_RANGE),
      )
    ) {
      return '24/7';
    }

    const weekday = formatRange('weekday');
    const saturday = formatRange('saturday');
    const sunday = formatRange('sunday');

    return `${weekday}, ${saturday}, ${sunday}`;
  }
}
